import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiArrowLeft, FiUploadCloud, FiX, FiPlus } from "react-icons/fi";
import api, { imgUrl } from "../utils/api";
import Loader from "../components/Loader";

const emptyForm = {
  title: "",
  type: "pooja",
  image: "",
  desc: "",
  priceFrom: "Contact",
  priceNote: "",
  duration: "",
  bestFor: [],
  includes: [],
  faqs: [],
  featured: true,
  order: 0,
  published: true,
};

export default function ServiceForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [includeInput, setIncludeInput] = useState("");
  const [faqQ, setFaqQ] = useState("");
  const [faqA, setFaqA] = useState("");

  useEffect(() => {
    if (isEdit) {
      api
        .get(`/services/admin/edit/${id}`)
        .then((res) => setForm(res.data))
        .catch(() => toast.error("Failed to load service"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  // --- Image upload ---
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("image", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, image: data.url });
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setForm({ ...form, image: "" });
  };

  // --- Best For tags ---
  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.bestFor.includes(tag)) {
      setForm({ ...form, bestFor: [...form.bestFor, tag] });
      setTagInput("");
    }
  };

  const removeTag = (idx) => {
    setForm({ ...form, bestFor: form.bestFor.filter((_, i) => i !== idx) });
  };

  // --- Includes list ---
  const addInclude = () => {
    const item = includeInput.trim();
    if (item && !form.includes.includes(item)) {
      setForm({ ...form, includes: [...form.includes, item] });
      setIncludeInput("");
    }
  };

  const removeInclude = (idx) => {
    setForm({ ...form, includes: form.includes.filter((_, i) => i !== idx) });
  };

  // --- FAQs ---
  const addFaq = () => {
    if (faqQ.trim() && faqA.trim()) {
      setForm({ ...form, faqs: [...form.faqs, { q: faqQ.trim(), a: faqA.trim() }] });
      setFaqQ("");
      setFaqA("");
    }
  };

  const removeFaq = (idx) => {
    setForm({ ...form, faqs: form.faqs.filter((_, i) => i !== idx) });
  };

  // --- Submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.desc) {
      toast.error("Title and description are required");
      return;
    }
    setSaving(true);
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (isEdit) {
        await api.put(`/services/${id}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/services", payload);
        toast.success("Service created");
      }
      navigate("/admin/services");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save service");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="max-w-3xl">
      <Link
        to="/admin/services"
        className="inline-flex items-center gap-1 text-saffron-600 text-sm font-medium mb-6 hover:gap-2 transition-all"
      >
        <FiArrowLeft /> Back to Services
      </Link>

      <h1 className="font-heading text-2xl text-maroon-800 mb-6">
        {isEdit ? "Edit Service" : "Add New Service"}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
        {/* Title & Type */}
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Title *</label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
              placeholder="e.g. Satyanarayan Mahapuja"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 bg-white"
            >
              <option value="pooja">Pooja Service (Saurabh)</option>
              <option value="astrology">Astrology / Palmistry (Gauri)</option>
            </select>
          </div>
        </div>

        {/* Image */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Service Image</label>
          <div className="flex flex-wrap gap-3 mb-3">
            {form.image && (
              <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                <img src={imgUrl(form.image)} alt="service" className="w-full h-full object-cover" />
                <button type="button" onClick={removeImage} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5">
                  <FiX size={12} />
                </button>
              </div>
            )}
          </div>
          <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-50 text-sm text-gray-600">
            <FiUploadCloud /> {uploading ? "Uploading..." : "Upload Image"}
            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
          </label>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description * (max 500 chars)</label>
          <textarea
            name="desc"
            value={form.desc}
            onChange={handleChange}
            rows={3}
            maxLength={500}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
            placeholder="Short description shown on the service card"
          />
        </div>

        {/* Price, Price Note, Duration, Order */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Price From</label>
            <input
              type="text"
              name="priceFrom"
              value={form.priceFrom}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
              placeholder="e.g. 5100 or Contact"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Price Note</label>
            <input
              type="text"
              name="priceNote"
              value={form.priceNote}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
              placeholder="e.g. Price varies by location"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <input
              type="text"
              name="duration"
              value={form.duration}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
              placeholder="e.g. 2 to 3 hours"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
            <input
              type="number"
              name="order"
              value={form.order}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-saffron-400"
              placeholder="0"
            />
          </div>
        </div>

        {/* Best For Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Best For (tags)</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {form.bestFor.map((tag, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 bg-saffron-100 text-saffron-700 px-3 py-1 rounded-full text-xs font-medium">
                {tag}
                <button type="button" onClick={() => removeTag(idx)} className="text-saffron-500 hover:text-saffron-700">
                  <FiX size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
              className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-sm"
              placeholder="Type a tag and press Enter"
            />
            <button type="button" onClick={addTag} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">
              <FiPlus />
            </button>
          </div>
        </div>

        {/* Includes List */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Included (checklist items)</label>
          <div className="space-y-2 mb-2">
            {form.includes.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <span className="flex-1 text-sm text-gray-700">{item}</span>
                <button type="button" onClick={() => removeInclude(idx)} className="text-red-500 hover:text-red-700">
                  <FiX size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={includeInput}
              onChange={(e) => setIncludeInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addInclude())}
              className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-sm"
              placeholder="Type an item and press Enter"
            />
            <button type="button" onClick={addInclude} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">
              <FiPlus />
            </button>
          </div>
        </div>

        {/* FAQs */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">FAQs</label>
          <div className="space-y-3 mb-3">
            {form.faqs.map((faq, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-medium text-gray-700">Q: {faq.q}</p>
                  <button type="button" onClick={() => removeFaq(idx)} className="text-red-500 hover:text-red-700 shrink-0">
                    <FiX size={14} />
                  </button>
                </div>
                <p className="text-sm text-gray-500">A: {faq.a}</p>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mb-2">
            <input
              type="text"
              value={faqQ}
              onChange={(e) => setFaqQ(e.target.value)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm"
              placeholder="Question"
            />
            <input
              type="text"
              value={faqA}
              onChange={(e) => setFaqA(e.target.value)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm"
              placeholder="Answer"
            />
          </div>
          <button type="button" onClick={addFaq} className="inline-flex items-center gap-1 text-sm text-saffron-600 font-medium">
            <FiPlus /> Add FAQ
          </button>
        </div>

        {/* Checkboxes */}
        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" name="published" checked={form.published} onChange={handleChange} className="w-4 h-4" />
            Published
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} className="w-4 h-4" />
            Featured (shown on homepage)
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 bg-maroon-700 hover:bg-maroon-800 disabled:opacity-60 text-gold-100 px-6 py-3 rounded-lg font-semibold transition-colors"
        >
          <FiSave /> {saving ? "Saving..." : "Save Service"}
        </button>
      </form>
    </div>
  );
}
